/**
 * MechanicClockBar — iter131 Phase 2
 * -----------------------------------------------------------------------------
 * Sticky 3-button bar (Clock-In / Break / Clock-Off) for the Mechanic Dashboard.
 * Polls `/api/mechanic/workday/today` every 15s and ticks the workshop-time
 * display every second.
 *
 * State transitions:
 *   off / no-workday → Clock-In active, Break/Off disabled
 *   on_clock         → Break + Clock-Off active, Clock-In disabled
 *   on_break         → Break (Resume) + Clock-Off active, Clock-In disabled
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Clock, Coffee, LogOut, LogIn, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMins = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

export default function MechanicClockBar({ onStateChange }) {
  const [wd, setWd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/mechanic/workday/today`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setWd(d.workday || null);
      if (onStateChange) onStateChange(d.workday);
    } catch (_e) { /* swallow */ }
  }, [onStateChange]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  // 1-second ticker for live durations
  useEffect(() => {
    if (!wd || wd.state === 'off') return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [wd]);

  const callApi = async (path, successMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/mechanic/${path}`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(errText || `HTTP ${r.status}`);
      }
      const data = await r.json();
      if (data.paused_job_card_id) {
        toast.success(`Break started — paused ${data.paused_job_card_id.slice(0, 12)}…`);
      } else if (data.resumed_job_card_id) {
        toast.success(`Back from break — resumed ${data.resumed_job_card_id.slice(0, 12)}…`);
      } else if (data.closed_job_card_id) {
        toast.success(`Clocked off — paused ${data.closed_job_card_id.slice(0, 12)}…`);
      } else if (Array.isArray(data.resumed_jc_ids) && data.resumed_jc_ids.length > 0) {
        // iter162 — Multi-day JC clocks that got auto-closed at 5pm the
        // night before are now resumed on today's clock-in. Toast so
        // the mechanic knows the clock is ticking again.
        toast.success(
          `${successMsg} — resumed ${data.resumed_jc_ids.length} job card${data.resumed_jc_ids.length === 1 ? '' : 's'}`,
          { duration: 5000 },
        );
      } else {
        toast.success(successMsg);
      }
      await load();
    } catch (e) {
      toast.error(`${e.message || e}`.slice(0, 200));
    } finally {
      setBusy(false);
    }
  };

  const state = wd?.state || 'off';

  // Live workshop minutes (ticks every second)
  let liveMin = 0;
  let liveBreakMin = wd?.break_minutes || 0;
  if (wd?.clock_in_at) {
    const start = new Date(wd.clock_in_at).getTime();
    const end = wd?.clock_out_at ? new Date(wd.clock_out_at).getTime() : Date.now();
    liveMin = Math.max(0, Math.floor((end - start) / 60000));
  }
  // Add running break time if currently on break
  if (state === 'on_break' && wd?.breaks?.length) {
    const openBreak = wd.breaks.find((b) => !b.end_at);
    if (openBreak) {
      const bs = new Date(openBreak.start_at).getTime();
      liveBreakMin = (wd.break_minutes || 0) + Math.max(0, Math.floor((Date.now() - bs) / 60000));
    }
  }
  const netMin = Math.max(0, liveMin - liveBreakMin);

  const isOff = state === 'off';
  const onClock = state === 'on_clock';
  const onBreak = state === 'on_break';

  // Color theme matched to state
  const themeBg = onBreak
    ? 'from-amber-50 to-orange-50 border-amber-300'
    : onClock
      ? 'from-emerald-50 to-green-50 border-emerald-300'
      : 'from-slate-50 to-slate-100 border-slate-300';

  return (
    <div
      data-testid="mechanic-clock-bar"
      className={`rounded-xl border-2 bg-gradient-to-r p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${themeBg}`}
    >
      {/* Status indicator */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            onBreak ? 'bg-amber-200' : onClock ? 'bg-emerald-200' : 'bg-slate-200'
          }`}
        >
          {onBreak ? (
            <Coffee className="h-5 w-5 text-amber-700 animate-pulse" />
          ) : onClock ? (
            <Clock className="h-5 w-5 text-emerald-700 animate-pulse" />
          ) : (
            <Clock className="h-5 w-5 text-slate-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={`text-[10px] uppercase tracking-wider font-bold ${
              onBreak ? 'text-amber-700' : onClock ? 'text-emerald-700' : 'text-slate-500'
            }`}
            data-testid="clock-bar-status-label"
          >
            {onBreak ? 'On break' : onClock ? 'Clocked in' : 'Not clocked in'}
          </p>
          <p className="text-base font-semibold text-slate-900 tabular-nums" data-testid="clock-bar-workshop-time">
            {isOff
              ? 'Tap Clock-In to start your shift'
              : (
                <>
                  <strong>{fmtMins(netMin)}</strong>
                  <span className="text-slate-500 font-normal">
                    {' '}of {fmtMins(liveMin)} workshop · {fmtMins(liveBreakMin)} break
                  </span>
                </>
              )}
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap">
        {isOff && (
          <button
            onClick={() => callApi('clock-in', 'Clocked in — let’s go!')}
            disabled={busy}
            data-testid="clock-in-btn"
            className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 shadow-sm"
          >
            <LogIn className="h-4 w-4" />
            Clock-In
          </button>
        )}
        {onClock && (
          <>
            <button
              onClick={() => callApi('break-start', 'Break started')}
              disabled={busy}
              data-testid="break-start-btn"
              className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 shadow-sm"
            >
              <Coffee className="h-4 w-4" />
              Break
            </button>
            <button
              onClick={() => callApi('clock-off', 'Clocked off — enjoy your evening!')}
              disabled={busy}
              data-testid="clock-off-btn"
              className="px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 shadow-sm"
            >
              <LogOut className="h-4 w-4" />
              Clock-Off
            </button>
          </>
        )}
        {onBreak && (
          <>
            <button
              onClick={() => callApi('break-end', 'Back from break')}
              disabled={busy}
              data-testid="break-end-btn"
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 shadow-sm"
            >
              <Play className="h-4 w-4" />
              End Break
            </button>
            <button
              onClick={() => callApi('clock-off', 'Clocked off')}
              disabled={busy}
              data-testid="clock-off-btn"
              className="px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 shadow-sm"
            >
              <LogOut className="h-4 w-4" />
              Clock-Off
            </button>
          </>
        )}
      </div>
    </div>
  );
}
